import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileText, LayoutGrid, List } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EstimatesWidget = () => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchEstimates();
  }, [statusFilter]);

  const fetchEstimates = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/director/estimates`, { params });
      setEstimates(response.data);
    } catch (error) {
      console.error('Error fetching estimates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { label: 'Draft', variant: 'secondary' },
      'sent': { label: 'Sent', variant: 'default' },
      'viewed': { label: 'Viewed', variant: 'outline' },
      'accepted': { label: 'Accepted', variant: 'success' },
      'declined': { label: 'Declined', variant: 'destructive' },
    };
    const config = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex justify-between items-center">
        <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
          <SelectTrigger className="w-[130px] h-8">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="viewed">Viewed</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex gap-1">
          <Button
            variant={viewMode === 'card' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('card')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Estimates */}
      {estimates.length > 0 ? (
        <div className={`max-h-72 overflow-y-auto ${
          viewMode === 'card' ? 'space-y-2' : 'space-y-1'
        }`}>
          {estimates.map((estimate, idx) => (
            <div
              key={idx}
              className={`border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition ${
                viewMode === 'card' ? 'p-3' : 'p-2'
              }`}
              onClick={() => navigate(`/leads/${estimate.id}`)}
            >
              {viewMode === 'card' ? (
                <>
                  <div className="font-semibold">{estimate.customer_name || 'Unknown Customer'}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Estimate #{estimate.id?.slice(0, 8)} • {formatCurrency(estimate.deal_value)}
                  </div>
                  <div className="flex gap-2 mt-2 items-center">
                    {getStatusBadge(estimate.status)}
                    <span className="text-xs text-gray-500">{estimate.days_in_status || 0} days</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <span className="font-semibold text-sm">{estimate.customer_name || 'Unknown'}</span>
                    <span className="text-sm text-gray-600 ml-2">{formatCurrency(estimate.deal_value)}</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    {getStatusBadge(estimate.status)}
                    <span className="text-xs text-gray-500">{estimate.days_in_status || 0}d</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
          <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No estimates found</p>
        </div>
      )}
    </div>
  );
};

export default EstimatesWidget;